import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/useAuthStore';
import { supabase } from '../../lib/supabase';
import { fechaEnBogota } from '../../lib/cierre/bogotaDate';
import { getGastosPeriodo, getIngresosPeriodo, getTopGastosCategoria, getTopLineasFactura } from '../../lib/cierre/queries';
import { aggregateToBalance, computeSaldoFinal, formatCOP, totalBalance } from '../../lib/cierre/calculations';
import { emptyBalance } from '../../lib/cierre/types';
import { downloadCierrePdf } from './cierrePdf';

function monthBounds(y: number, mo: number): { desde: string; hasta: string } {
  const desde = `${y}-${String(mo).padStart(2, '0')}-01`;
  const last = new Date(y, mo, 0).getDate();
  const hasta = `${y}-${String(mo).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { desde, hasta };
}

export const CierreAnualView: React.FC = () => {
  const { currentEmpresa } = useAuthStore();
  const empresaId = currentEmpresa?.id;
  const y0 = parseInt(fechaEnBogota().slice(0, 4), 10);
  const [anio, setAnio] = useState(y0);
  const [ingresos, setIngresos] = useState(emptyBalance());
  const [gastos, setGastos] = useState(emptyBalance());
  const [mesRows, setMesRows] = useState<{ mes: number; ing: number; gas: number }[]>([]);
  const [topProd, setTopProd] = useState<{ nombre: string; total: number }[]>([]);
  const [topCat, setTopCat] = useState<{ categoria: string; total: number }[]>([]);
  const [prevYearNet, setPrevYearNet] = useState<number | null>(null);
  const [thisYearNet, setThisYearNet] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const yearBounds = useMemo(() => {
    return { desde: `${anio}-01-01`, hasta: `${anio}-12-31` };
  }, [anio]);

  const load = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setErr(null);
    try {
      const { desde, hasta } = yearBounds;
      const prevDesde = `${anio - 1}-01-01`;
      const prevHasta = `${anio - 1}-12-31`;

      const [ingLines, gasLines, ingPrev, gasPrev] = await Promise.all([
        getIngresosPeriodo(supabase, empresaId, desde, hasta),
        getGastosPeriodo(supabase, empresaId, desde, hasta),
        getIngresosPeriodo(supabase, empresaId, prevDesde, prevHasta),
        getGastosPeriodo(supabase, empresaId, prevDesde, prevHasta),
      ]);

      const ing = aggregateToBalance(ingLines);
      const gas = aggregateToBalance(gasLines);
      setIngresos(ing);
      setGastos(gas);

      const rows = await Promise.all(
        Array.from({ length: 12 }, async (_, idx) => {
          const mo = idx + 1;
          const b = monthBounds(anio, mo);
          const [il, gl] = await Promise.all([
            getIngresosPeriodo(supabase, empresaId, b.desde, b.hasta),
            getGastosPeriodo(supabase, empresaId, b.desde, b.hasta),
          ]);
          return {
            mes: mo,
            ing: totalBalance(aggregateToBalance(il)),
            gas: totalBalance(aggregateToBalance(gl)),
          };
        })
      );
      setMesRows(rows);

      const [prod, cat] = await Promise.all([
        getTopLineasFactura(supabase, empresaId, desde, hasta, 15),
        getTopGastosCategoria(supabase, empresaId, desde, hasta, 10),
      ]);
      setTopProd(prod);
      setTopCat(cat);

      const ingPY = aggregateToBalance(ingPrev);
      const gasPY = aggregateToBalance(gasPrev);
      setThisYearNet(totalBalance(ing) - totalBalance(gas));
      setPrevYearNet(totalBalance(ingPY) - totalBalance(gasPY));
    } catch (e) {
      console.error(e);
      setErr(e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [empresaId, anio, yearBounds]);

  useEffect(() => {
    void load();
  }, [load]);

  const netoPorMetodo = useMemo(
    () => computeSaldoFinal(emptyBalance(), ingresos, gastos),
    [ingresos, gastos]
  );

  const yoy =
    prevYearNet != null && prevYearNet !== 0 && thisYearNet != null
      ? Math.round(((thisYearNet - prevYearNet) / Math.abs(prevYearNet)) * 1000) / 10
      : null;

  if (!empresaId) return <p className="text-gray-600">Selecciona una empresa.</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1F4A]">Cierre anual</h1>
          <p className="text-sm text-gray-600">Resumen por año calendario (Bogotá).</p>
        </div>
        <label className="text-sm">
          Año
          <input
            type="number"
            className="ml-1 w-28 rounded border border-gray-300 px-2 py-1"
            value={anio}
            onChange={(e) => setAnio(parseInt(e.target.value, 10) || anio)}
          />
        </label>
        <button
          type="button"
          onClick={() =>
            downloadCierrePdf({
              titulo: 'Anual',
              periodo: String(anio),
              empresaNombre: currentEmpresa?.nombre || 'Empresa',
              ingresos,
              gastos,
              netoPorMetodo,
              extraLines: [
                `Neto año: ${thisYearNet != null ? formatCOP(thisYearNet) : '—'}`,
                prevYearNet != null ? `Neto año anterior: ${formatCOP(prevYearNet)}` : '',
                yoy != null ? `YoY: ${yoy}%` : '',
              ].filter(Boolean),
            })
          }
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800"
        >
          Exportar PDF
        </button>
      </div>

      {err ? <div className="rounded bg-red-50 p-3 text-red-800">{err}</div> : null}
      {loading ? <p className="text-gray-500">Cargando…</p> : null}

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-2 font-semibold text-gray-900">Desglose por mes</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 pr-4">Mes</th>
                <th className="py-2 pr-4 text-right">Ingresos</th>
                <th className="py-2 pr-4 text-right">Gastos</th>
                <th className="py-2 text-right">Neto</th>
              </tr>
            </thead>
            <tbody>
              {mesRows.map((r) => (
                <tr key={r.mes} className="border-b border-gray-100">
                  <td className="py-2">{r.mes.toString().padStart(2, '0')}</td>
                  <td className="py-2 text-right tabular-nums">{formatCOP(r.ing)}</td>
                  <td className="py-2 text-right tabular-nums">{formatCOP(r.gas)}</td>
                  <td className="py-2 text-right font-medium tabular-nums">{formatCOP(r.ing - r.gas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-2 font-semibold text-gray-900">Año vs año anterior</h2>
        <p className="text-sm">Neto {anio}: <strong>{thisYearNet != null ? formatCOP(thisYearNet) : '—'}</strong></p>
        <p className="text-sm">Neto {anio - 1}: <strong>{prevYearNet != null ? formatCOP(prevYearNet) : '—'}</strong></p>
        {yoy != null ? (
          <p className={`text-sm font-semibold ${yoy >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            Variación YoY: {yoy >= 0 ? '↑' : '↓'} {Math.abs(yoy)}%
          </p>
        ) : null}
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-2 font-semibold text-gray-900">Top líneas de factura (año)</h2>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {topProd.map((r) => (
              <li key={r.nombre} className="flex justify-between gap-2">
                <span className="truncate">{r.nombre}</span>
                <span className="tabular-nums">{formatCOP(r.total)}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-2 font-semibold text-gray-900">Top gastos por categoría (año)</h2>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {topCat.map((r) => (
              <li key={r.categoria} className="flex justify-between gap-2">
                <span className="truncate">{r.categoria}</span>
                <span className="tabular-nums">{formatCOP(r.total)}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
};
